import React, { useState, useEffect } from 'react';
import { apiFetch, parseApiResponse } from '../../api';
import { getStudentDisplayName } from '../../utils/studentName';

/**
 * ManualTranscriptForm - Create transcripts without Google Meet
 *
 * Allows admins to paste or type transcript content directly,
 * enabling demos and testing without Google Workspace integration.
 */
const ManualTranscriptForm = ({
  students,
  onTranscriptCreated,
  onViewUploaded,
  onSummarizeAndView
}) => {
  const [title, setTitle] = useState('');
  const [transcriptContent, setTranscriptContent] = useState('');
  const [studentUuid, setStudentUuid] = useState('');
  const [sessionDate, setSessionDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [createdTranscript, setCreatedTranscript] = useState(null);

  // Set default date to today
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setSessionDate(today);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setCreatedTranscript(null);

    // Validation
    if (!title.trim()) {
      setError('Please enter a session title');
      return;
    }
    if (!transcriptContent.trim()) {
      setError('Please enter the transcript content');
      return;
    }

    setSubmitting(true);

    try {
      const response = await apiFetch('/api/google/meet/transcripts/manual', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title.trim(),
          transcript_content: transcriptContent.trim(),
          student_uuid: studentUuid || null,
          session_date: sessionDate ? `${sessionDate}T12:00:00Z` : null
        })
      });
      const result = await parseApiResponse(response, 'manual-transcript-create');
      if (!result.ok) {
        throw new Error(result.error.message || 'Failed to create transcript');
      }
      const data = result.data;

      setSuccess('Transcript created successfully! You can now generate a summary.');
      setCreatedTranscript(data.transcript);

      // Reset form
      setTitle('');
      setTranscriptContent('');
      setStudentUuid('');

      // Notify parent
      if (onTranscriptCreated) {
        onTranscriptCreated(data.transcript);
      }

    } catch (err) {
      console.error('Error creating manual transcript:', err);
      setError(err.message || 'Failed to create transcript');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUseSample = () => {
    setTitle('Sample Session - Math Foundations');
    setTranscriptContent(`Tutor: Good afternoon! How are you feeling about math today?

Student: Hi! I'm okay, but I'm still confused about fractions.

Tutor: That's totally understandable. Fractions can be tricky. Let's start by reviewing what we covered last time. Can you tell me what the top number of a fraction is called?

Student: Umm... the numerator?

Tutor: Exactly right! And the bottom number?

Student: The denominator.

Tutor: Perfect! You're remembering well. Now, let's work on adding fractions. If I have 1/4 plus 1/4, what do you think we get?

Student: Is it 2/4?

Tutor: Yes! Great job. And can we simplify 2/4?

Student: Oh, that's 1/2!

Tutor: Excellent work! You're making great progress. Let's try a few more examples to build your confidence.

Student: Okay, I think I'm starting to get it now.

Tutor: That's the spirit! Remember, making mistakes is part of learning. You're doing great.`);
  };

  return (
    <div className="manual-transcript-form">
      <div className="form-header">
        <h3>Create Manual Transcript</h3>
        <p>Enter a transcript directly without Google Meet sync</p>
      </div>

      {error && (
        <div className="form-message error">
          <span className="message-icon">!</span>
          {error}
        </div>
      )}

      {success && (
        <div className="form-message success">
          <span className="message-icon">OK</span>
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="manual-title">Session Title *</label>
            <input
              type="text"
              id="manual-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Math Session - Fractions Review"
              disabled={submitting}
              maxLength={200}
            />
          </div>

          <div className="form-group">
            <label htmlFor="manual-date">Session Date</label>
            <input
              type="date"
              id="manual-date"
              value={sessionDate}
              onChange={(e) => setSessionDate(e.target.value)}
              disabled={submitting}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="manual-student">Link to Student (Optional)</label>
          <select
            id="manual-student"
            value={studentUuid}
            onChange={(e) => setStudentUuid(e.target.value)}
            disabled={submitting}
          >
            <option value="">-- Select a student --</option>
            {students && students.map((student) => (
              <option key={student.uuid} value={student.uuid}>
                {getStudentDisplayName(student)}
              </option>
            ))}
          </select>
          <span className="form-hint">
            Linking connects this transcript to a student's profile for session planning
          </span>
        </div>

        <div className="form-group">
          <label htmlFor="manual-content">
            Transcript Content *
            <button
              type="button"
              className="sample-btn"
              onClick={handleUseSample}
              disabled={submitting}
            >
              Use Sample
            </button>
          </label>
          <textarea
            id="manual-content"
            value={transcriptContent}
            onChange={(e) => setTranscriptContent(e.target.value)}
            placeholder="Paste or type the session transcript here...

Format suggestion:
Tutor: [tutor dialogue]
Student: [student dialogue]

Include conversation, questions, explanations, and student responses."
            rows={16}
            disabled={submitting}
          />
          <span className="form-hint">
            {transcriptContent.length > 0 && `${transcriptContent.length} characters`}
          </span>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary btn-large"
            disabled={submitting || !title.trim() || !transcriptContent.trim()}
          >
            {submitting ? 'Creating...' : 'Create Transcript'}
          </button>
          {createdTranscript?.id && (
            <>
              <button
                type="button"
                className="btn btn-secondary btn-large"
                onClick={() => onViewUploaded && onViewUploaded(createdTranscript)}
                disabled={submitting}
              >
                View Uploaded Transcript
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-large"
                onClick={() => onSummarizeAndView && onSummarizeAndView(createdTranscript)}
                disabled={submitting}
              >
                Summarize & View
              </button>
            </>
          )}
        </div>
      </form>

      <div className="form-tips">
        <h4>Tips for good transcripts:</h4>
        <ul>
          <li>Include both tutor and student dialogue with clear speaker labels</li>
          <li>Capture questions asked and how the student responded</li>
          <li>Note moments of confusion or breakthroughs</li>
          <li>Include any teaching methods or tools used</li>
        </ul>
      </div>
    </div>
  );
};

export default ManualTranscriptForm;
